/*
 * Copyright © 2017-2026 Knowledge for Decisions (K4D). All rights reserved.
 *
 * This code and associated materials are protected by copyright laws and
 * international treaties. Unauthorized use, reproduction, distribution, or
 * modification of any content from Knowledge for Decisions (K4D) is strictly
 * prohibited. For inquiries regarding the use or licensing of K4D's
 * copyrighted materials, please contact us at contact@k4d.com.br. "Knowledge
 * for Decisions (K4D)" and the slogan "Using data to make precise decisions"
 * are registered trademarks of Knowledge for Decisions (K4D).
 */

import { toast } from 'sonner';
import { leadAdErrorCode, leadAdErrorDetail, type LeadAdErrorCode } from '@/shared/utils/ad-dialog-helpers';

/**
 * Resolves the display label for a portal.
 */
function getPortalLabel(portal: string, t: (key: string, opts?: Record<string, unknown>) => string): string {
  const keyMap: Record<string, string> = {
    olx: 'publish.portals.olxLabel',
    zap: 'publish.portals.zapLabel',
    vivareal: 'publish.portals.vivarealLabel',
    chavesnamao: 'publish.portals.chavesnamaoLabel',
    facebook: 'publish.portals.facebookLabel',
    instagram: 'publish.portals.instagramLabel',
  };
  const key = keyMap[portal];
  return key ? t(key) : portal;
}

type PortalResult = {
  status: string;
  reason?: string;
  /** Only present when a Lead Ad was requested for this portal. */
  lead_ad_status?: 'created' | 'failed';
  /** Only present when lead_ad_status is "failed". */
  lead_ad_error?: string;
};
type Results = Record<string, PortalResult>;

export type { PortalResult, Results };

const SUCCESS_STATUSES = new Set(['published', 'queued', 'scheduled']);

/**
 * Classifies publish results into success and failure.
 */
export function classifyPublishResults(results: Results): {
  successes: string[];
  failures: Array<{ portal: string; reason?: string }>;
} {
  const successes: string[] = [];
  const failures: Array<{ portal: string; reason?: string }> = [];
  for (const [portal, r] of Object.entries(results)) {
    if (SUCCESS_STATUSES.has(r.status)) successes.push(portal);
    else failures.push({ portal, reason: r.reason });
  }
  return { successes, failures };
}

/**
 * Converts a scheduled datetime into an ISO string.
 */
export function toScheduledAtIso(localValue: string): string {
  const [datePart, timePart] = localValue.split('T');
  const [year, month, day] = datePart.split('-').map(Number);
  const [hour, minute] = (timePart || '00:00').split(':').map(Number);
  return new Date(year, month - 1, day, hour, minute).toISOString();
}

/**
 * Returns whether a scheduled time is in the future.
 */
export function isScheduledAtInFuture(localValue: string): boolean {
  if (!localValue) return false;
  return new Date(toScheduledAtIso(localValue)).getTime() > Date.now();
}

/**
 * Computes the minimum selectable schedule datetime.
 */
export function scheduleDatetimeLocalMin(now = new Date()): string {
  const pad = (n: number) => String(n).padStart(2, '0');
  const y = now.getFullYear();
  const mo = pad(now.getMonth() + 1);
  const d = pad(now.getDate());
  const h = pad(now.getHours());
  const mi = pad(now.getMinutes());
  return `${y}-${mo}-${d}T${h}:${mi}`;
}

/**
 * Builds the validation message for a bad schedule.
 */
export function scheduleErrorMessage(
  error: unknown,
  t: (key: string) => string,
): string {
  const code = (error as { response?: { data?: { error?: string } } })?.response?.data?.error;
  if (code === 'scheduled_at_must_be_future') return t('publish.schedulePastError');
  if (code === 'invalid_scheduled_at') return t('publish.reason.invalid_scheduled_at');
  return t('publish.errorToast');
}

/**
 * Formats a portal failure into a display label.
 */
function formatFailureLabel(
  portal: string,
  reason: string | undefined,
  t: (key: string, opts?: Record<string, unknown>) => string,
): string {
  const label = getPortalLabel(portal, t);
  if (!reason) return label;
  const reasonKey = `publish.reason.${reason}`;
  const reasonLabel = t(reasonKey as any);
  const isTranslated = reasonLabel && reasonLabel !== reasonKey;
  return isTranslated ? `${label} (${reasonLabel})` : label;
}

/**
 * Shows a toast summarizing the publish results.
 */
export function showPublishResultToast(
  results: Results,
  t: (key: string, opts?: Record<string, unknown>) => string,
): boolean {
  const { successes, failures } = classifyPublishResults(results);
  const allFailed = successes.length === 0;
  const anyFailed = failures.length > 0;

  if (allFailed) {
    const labels = failures.map(({ portal, reason }) => formatFailureLabel(portal, reason, t)).join(', ');
    toast.error(t('publish.allFailedToast', { portals: labels }));
    return false;
  }

  if (anyFailed) {
    const successLabels = successes.map((p) => getPortalLabel(p, t)).join(', ');
    const failureLabels = failures.map(({ portal, reason }) => formatFailureLabel(portal, reason, t)).join(', ');
    toast.warning(t('publish.partialSuccessToast', { successes: successLabels, failures: failureLabels }));
    return true;
  }

  toast.success(t('publish.successToast'));
  return true;
}

/**
 * Finds whether any portal's Lead Ad specifically failed to be created --
 * independent of whether the post/listing itself published successfully,
 * since a broker who opted into a Lead Ad otherwise has no way to know it
 * silently never got created.
 *
 * @param results - The per-portal publish results.
 * @returns Null when no Lead Ad failed; otherwise Meta's explanation (null when it gave none)
 *   and the "could not be attempted" code when that is why it failed.
 */
export function findLeadAdFailure(
  results: Results,
): { detail: string | null; code: LeadAdErrorCode | null } | null {
  const failed = Object.values(results).filter((r) => r.lead_ad_status === 'failed');
  if (failed.length === 0) return null;
  return {
    detail: failed.map((r) => leadAdErrorDetail(r.lead_ad_error)).find(Boolean) ?? null,
    code: failed.map((r) => leadAdErrorCode(r.lead_ad_error)).find(Boolean) ?? null,
  };
}

/**
 * Whether any portal's Lead Ad was created -- the confirmation the user
 * otherwise never gets, since a successful Lead Ad changes nothing in the
 * publish dialog itself.
 *
 * @param results - The per-portal publish results.
 * @returns True when at least one Lead Ad was created.
 */
export function findLeadAdCreated(results: Results): boolean {
  return Object.values(results).some((r) => r.lead_ad_status === 'created');
}
